import { normalizeGeneratedHtml } from './html-qc';

function hasAccessibleName(el: Element): boolean {
  const ariaLabel = (el.getAttribute('aria-label') ?? '').trim();
  if (ariaLabel) return true;
  const ariaLabelledBy = (el.getAttribute('aria-labelledby') ?? '').trim();
  if (ariaLabelledBy) return true;

  const text = (el.textContent ?? '').replace(/\s+/g, ' ').trim();
  if (text) return true;

  const imgAlt = Array.from(el.querySelectorAll('img'))
    .map((img) => (img.getAttribute('alt') ?? '').trim())
    .find(Boolean);
  if (imgAlt) return true;

  const title = (el.getAttribute('title') ?? '').trim();
  if (title) return true;

  return false;
}

function renameTag(el: Element, tagName: string): Element {
  const doc = el.ownerDocument;
  if (!doc) return el;
  const next = doc.createElement(tagName);
  for (const { name, value } of Array.from(el.attributes)) {
    next.setAttribute(name, value);
  }
  while (el.firstChild) next.appendChild(el.firstChild);
  el.replaceWith(next);
  return next;
}

function niceLabelFromId(id: string) {
  const cleaned = id
    .trim()
    .replace(/[_\-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return '';
  return cleaned.slice(0, 1).toUpperCase() + cleaned.slice(1);
}

function setAriaLabelIfMissing(el: Element, fallback: string) {
  if (hasAccessibleName(el)) return;
  const label = fallback.trim() || 'Action';
  el.setAttribute('aria-label', label);
}

function isValidHex(hex: string) {
  return /^#[0-9a-f]{6}$/i.test(hex.trim());
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const v = m[1];
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return null;
  return { r, g, b };
}

function relativeLuminance(rgb: { r: number; g: number; b: number }) {
  const toLinear = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const R = toLinear(rgb.r);
  const G = toLinear(rgb.g);
  const B = toLinear(rgb.b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

function isLightHex(hex: string) {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  return relativeLuminance(rgb) >= 0.5;
}

function patchRootTheme(rootEl: HTMLElement, backgroundColor: string) {
  const bg = backgroundColor.trim();
  if (!isValidHex(bg)) return;

  const light = isLightHex(bg);
  rootEl.style.backgroundColor = bg;
  rootEl.style.backgroundImage = 'none';

  const cls = (rootEl.getAttribute('class') ?? '').trim();
  if (!cls) return;

  const tokens = cls.split(/\s+/g).filter(Boolean);
  const filtered = tokens.filter((t) => {
    if (/^bg-gradient-/.test(t)) return false;
    if (/^(from|via|to)-/.test(t)) return false;
    // Remove obvious "full screen dark" backdrops on the root.
    if (/^bg-(black|neutral-9\d\d|gray-9\d\d|slate-9\d\d|zinc-9\d\d|stone-9\d\d)$/.test(t)) return false;
    return true;
  });

  const withoutText = filtered.filter((t) => !/^text-/.test(t));
  if (light) {
    withoutText.push('text-slate-900');
  } else {
    withoutText.push('text-slate-50');
  }

  rootEl.setAttribute('class', Array.from(new Set(withoutText)).join(' '));
}

function unwrapNestedInteractive(root: HTMLElement) {
  // Fix invalid nesting: <a><button>...</button></a>, <button><a>...</a></button>, <a><a>...</a></a>, <button><button>...</button></button>
  const pairs: Array<[string, string]> = [
    ['a[href]', 'button'],
    ['button', 'a[href]'],
    ['a[href]', 'a[href]'],
    ['button', 'button'],
  ];

  for (const [outerSel, innerSel] of pairs) {
    const outers = Array.from(root.querySelectorAll<HTMLElement>(outerSel));
    for (const outer of outers) {
      const inners = Array.from(outer.querySelectorAll<HTMLElement>(innerSel));
      for (const inner of inners) {
        // Only handle true nesting (skip if same element).
        if (inner === outer) continue;
        // Replace inner interactive element with a span that keeps its classes/content.
        const span = root.ownerDocument.createElement('span');
        const innerClass = inner.getAttribute('class');
        if (innerClass) span.setAttribute('class', innerClass);
        span.innerHTML = inner.innerHTML;
        inner.replaceWith(span);
      }
    }
  }
}

export function improveHtmlLocally(params: { html: string; backgroundColor?: string }): string {
  const normalized = normalizeGeneratedHtml(params.html);
  try {
    const doc = new DOMParser().parseFromString(`<div id="__root__">${normalized}</div>`, 'text/html');
    const root = doc.getElementById('__root__') as HTMLElement | null;
    if (!root) return normalized;

    // Ensure images have alt attribute.
    root.querySelectorAll('img').forEach((img) => {
      if (!img.hasAttribute('alt')) img.setAttribute('alt', '');
    });

    // Ensure 1x h1.
    const h1s = Array.from(root.querySelectorAll('h1'));
    if (h1s.length === 0) {
      const firstHeading = root.querySelector('h2,h3,h4,h5,h6');
      if (firstHeading) {
        renameTag(firstHeading, 'h1');
      } else {
        const h1 = doc.createElement('h1');
        h1.setAttribute('class', 'sr-only');
        h1.textContent = 'Screen';
        root.prepend(h1);
      }
    } else if (h1s.length > 1) {
      for (const el of h1s.slice(1)) renameTag(el, 'h2');
    }

    // Fix nested interactive markup.
    unwrapNestedInteractive(root);

    // Ensure accessible names for icon-only buttons/links.
    const interactive = Array.from(root.querySelectorAll('button,a[href]'));
    interactive.forEach((el, idx) => {
      if (hasAccessibleName(el)) return;
      const id = (el.getAttribute('id') ?? '').trim();
      const fromId = id ? niceLabelFromId(id) : '';
      const svgTitle = Array.from(el.querySelectorAll('svg title'))
        .map((t) => (t.textContent ?? '').trim())
        .find(Boolean);
      const label = fromId || svgTitle || `Action ${idx + 1}`;
      setAriaLabelIfMissing(el, label);
    });

    // If user specified app background color, patch the top-level wrapper to match.
    const bg = (params.backgroundColor ?? '').trim();
    if (isValidHex(bg)) {
      const first = root.firstElementChild as HTMLElement | null;
      if (first) patchRootTheme(first, bg);
    }

    return normalizeGeneratedHtml(root.innerHTML);
  } catch {
    return normalized;
  }
}

